import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, OnDestroy, Output } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import moment from 'moment';

import { Subject, exhaustMap, from, takeUntil } from 'rxjs';

import { FullStoryService } from 'app/modules/full-story/services/full-story.service';
import { BridgeAPIService } from 'app/providers/bridge-api.service';
import { LoggerService } from 'app/services/logger.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { WrappedRequest } from '../api.service';
import { RealtimeService } from '../realtime.service';
import { GroupService } from '../semantria-api/group.service';
import { SemantriaAccountService } from '../semantria-api/semantria-account.service';
import { StorageAccountService } from '../storage-api/blender-account.service';
import { FieldService } from '../storage-api/field.service';
import { getNickname, setNickname } from './social-login.service';

export interface Permission {
  permission_id?: string;
  permission_type: string;
  config_id?: string;
  external_id?: string;
  group_id?: string;
  model_id?: string;
  user_id?: string;
  evaluation?: boolean;
  removed?: boolean;
}

export interface Session {
  id: string;
  info: {
    user_id: string;
  };
  info2: {
    token: string;
    is_expired: boolean;
    expires_at: string;
    permissions: Permission[];
    account_id: string;
    user_id: string;
  };
  user: {
    id: string;
    account_id: string;
    first_name: string;
    last_name: string;
    name: string;
    email_address: string;
    user_name: string;
    is_admin: boolean;
  };
  rt_token: string;
  spotlightEnvironmentLink: string;
}

export interface LogoutInterceptor {
  beforeLogout(): Promise<boolean>;
}

export interface ExpirationEvent {
  expiration: moment.Moment;
}

export interface LogoutResponse {
  redirectTo?: string;
}

export interface LogoutEvent {
  clear: boolean;
  reason: string;
  redirectTo?: string;
}

class FreshnessGuard<T> extends WrappedRequest<T> {
  private expires: moment.Moment;

  constructor(
    private state: T,
    ttl = 600,
    unit: moment.unitOfTime.DurationConstructor = 'seconds'
  ) {
    super();
    this.expires = moment().add(ttl, unit);
  }

  get(): T {
    return this.state;
  }

  isStale(): boolean {
    return !this.state || moment().isAfter(this.expires);
  }
}

@EnableClassTelemetry()
@Injectable({ providedIn: 'root' })
export class AuthService extends BridgeAPIService implements OnDestroy {
  EXPIRATION_WARNING_MINUTES = 15;
  expirationTimeout: NodeJS.Timeout;
  expirationWarningTimeout: NodeJS.Timeout;

  private sessionGuard: FreshnessGuard<Session | undefined> = new FreshnessGuard<
    Session | undefined
  >(undefined);
  private sessionUpdating = false;

  public isDemoMode = false; // todo: this functionality not used anymore
  public isSSOUser = false;
  public nickname = undefined;
  public isAccountAdmin = false;
  public isSharedSession = false;

  private destroy$ = new Subject<void>();

  constructor(
    http: HttpClient,
    systemService: SystemService,
    private fullStoryService: FullStoryService,
    private realtimeService: RealtimeService,
    private groupService: GroupService,
    private accountService: StorageAccountService,
    private logger: LoggerService
  ) {
    super(http, systemService);
    this.injectSessionTokenIfNeeded();
    this.realtimeService.renewToken$
      .pipe(
        exhaustMap(() => from(this.renewRtrToken())),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @Output() loggedout: EventEmitter<LogoutEvent> = new EventEmitter();
  @Output() sessionWarning: EventEmitter<ExpirationEvent> = new EventEmitter();
  logoutInterceptors: LogoutInterceptor[] = [];

  public injectSessionTokenIfNeeded(): boolean {
    return false;
  }

  public async redeemDashToken(encryptedToken: string): Promise<Session> {
    const url = this.getBaseUrl() + '/session/redeem-dash-token';
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dashToken: encryptedToken }),
    });
    if (!response.ok) {
      throw new Error('ShareLinkExpired');
    }
    const session = (await response.json()) as Session;
    this.setToken(session.id);
    this.setSession(session);
    return session;
  }

  public async checkPendingLogin(): Promise<Session | null> {
    const url = this.getBaseUrl() + '/session/pending-login';
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: this.getToken() },
      });
    } catch {
      return null;
    }
    if (response.status === 204) {
      return null;
    }
    if (response.status === 401) {
      throw new Error('SessionExpired');
    }
    if (!response.ok) {
      return null;
    }
    const session = (await response.json()) as Session;
    this.setToken(session.id);
    this.setSession(session);
    return session;
  }

  private async redirectToEnvironment(_environmentLink: string): Promise<void> {
    const response = await this.get<{ redirectUrl: string }>('/session/login-code');
    window.location.replace(response.redirectUrl);
  }

  private setSession(session: Session): void {
    if (!session) {
      this.sessionGuard = new FreshnessGuard(undefined);
      this.isSSOUser = false;
      this.isAccountAdmin = false;
      this.isSharedSession = false;
      this.realtimeService.setToken(undefined);
      setNickname.bind(this)(undefined);
    } else {
      if (
        session.spotlightEnvironmentLink &&
        window.location.origin !== session.spotlightEnvironmentLink
      ) {
        this.redirectToEnvironment(session.spotlightEnvironmentLink);
      }

      this.sessionGuard = new FreshnessGuard(session, 120, 'seconds');
      const lastAccountId = this.accountId;
      if (lastAccountId && lastAccountId !== session.info2.account_id) {
        this.logger.info('found stale accountId');
        this.groupService.clear();
        (window as any).appVolatile.redirectTo = undefined;
      }
      this.localStorage.setItem('accountId', session.info2.account_id, 'local', true);

      this.nickname = getNickname.bind(this)();
      this.isSSOUser = this.sessionHasUserPermission('SSV_SOCIAL_USER');
      this.isAccountAdmin = this.sessionHasUserPermission('ACCOUNT_ADMIN');
      this.isSharedSession = session.info2.user_id.startsWith('shared-session||');

      if (session.rt_token) {
        this.realtimeService.setToken(session.rt_token);
      }
      const appVolatile = (window as any).appVolatile;
      appVolatile.account_id = session.info2.account_id;

      const expiration = moment(session.info2.expires_at);
      this.setExpiration(expiration);
    }
  }

  public get accountId(): string {
    return this.localStorage.getItem('accountId');
  }

  public get currentUserId(): string | undefined {
    return this.sessionGuard.get()?.info2?.user_id;
  }

  public get isReadOnly(): boolean {
    return (
      this.sessionHasGroupPermission('READ_ONLY') ||
      this.sessionHasGroupPermission('EXECUTIVE_READ_ONLY') ||
      this.sessionHasUserPermission('READ_ONLY_SSV')
    );
  }

  public canGenerateRevealInsights(): boolean {
    return !this.isReadOnly || this.sessionHasUserPermission('SPOTLIGHT_REVEAL_ALLOWED');
  }

  public get isDebugUser(): boolean {
    return this.sessionHasUserPermission('SPOTLIGHT_DEBUG');
  }

  public get isExecutive() {
    return this.sessionHasGroupPermission('EXECUTIVE_READ_ONLY') || this.isSharedSession;
  }

  public get isPlaybackAllowed() {
    return this.isAccountAdmin || this.sessionHasUserPermission('PLAYBACK_ALLOWED');
  }

  public get hasAllAccess() {
    // "All Access" users are those who are not read-only and not executive read-only
    // They have full access to the group without being an account admin
    return !this.isReadOnly && !this.isExecutive;
  }

  public get isAlwaysRedact() {
    return this.sessionHasUserPermission('ALWAYS_REDACT');
  }

  public hasPermission(permission: string): boolean {
    return this.sessionHasUserPermission(permission) || this.sessionHasGroupPermission(permission);
  }

  public hasGroupPermission(permission: string): boolean {
    return this.sessionHasGroupPermission(permission);
  }

  private sessionHasUserPermission(perm: string): boolean {
    const session = this.sessionGuard.get();
    if (!session) {
      return false;
    }
    return session.info2.permissions.some(
      (p) => p.permission_type === perm || p.external_id === perm
    );
  }

  private sessionHasGroupPermission(permission: string): boolean {
    const session = this.sessionGuard.get();
    if (!session) {
      return false;
    }
    let currentGroup = this.groupService.getSelectedGroupId();
    if (!currentGroup) {
      currentGroup = this.localStorage.getItem('group');
    }
    //Group hasn't loaded yet.  Don't allow for anything that would depend on the this check.
    if (!currentGroup || !session) {
      return false;
    }
    const groupPermissions = session.info2.permissions
      .filter((perm) => perm.group_id && perm.permission_type === permission)
      .map((perm) => perm.group_id);
    return groupPermissions.includes(currentGroup);
  }

  public isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token || token === 'undefined') {
      return false;
    }

    return true;
  }

  public async login(
    login: string,
    password: string,
    nickname?: string,
    modifier?: string
  ): Promise<Session> {
    const response = await this.post<Session>(
      '/session',
      { login, password, modifier },
      {},
      {
        nickname,
      }
    );

    const token = response.id;
    this.setToken(token);
    this.setSession(response);

    return response;
  }

  public extendSession(): void {
    if (!this.sessionUpdating) {
      this.sessionUpdating = true;
      this.updateSession().then(() => {
        this.logger.info('session extended');
        this.sessionUpdating = false;
      });
    }
  }

  public async updateSession(): Promise<Session> {
    const response = await this.get<Session>('/session/_update');

    const token = response.id;
    this.setToken(token);
    this.setSession(response);

    return response;
  }

  public async renewRtrToken(): Promise<void> {
    const response = await this.get<{ rt_token: string }>('/session/rt_token');
    const rtToken = response.rt_token;
    const currentSession = this.sessionGuard.get();
    currentSession.rt_token = rtToken;
    this.setSession(currentSession);
  }

  public async logout(): Promise<void> {
    let confirmed = true;
    for (const ic of this.logoutInterceptors) {
      confirmed = confirmed && (await ic.beforeLogout());
    }

    if (!confirmed) {
      return;
    }
    let redirectUrl: string;
    if (!this.isSharedSession) {
      try {
        const response = await this.delete<LogoutResponse>('/session');
        redirectUrl = response.redirectTo;
      } catch (err) {
        // ignore the error and logout
      }
    }
    this.clear(undefined, redirectUrl);
  }

  public setExpiration(expiration: moment.Moment) {
    const secondsUntilExpiration = expiration.diff(moment());
    const secondsUntilWarning =
      expiration.diff(moment()) - this.EXPIRATION_WARNING_MINUTES * 60 * 1000;
    if (this.expirationTimeout) {
      clearTimeout(this.expirationTimeout);
    }
    this.expirationTimeout = setTimeout(() => {
      this.clear('SessionExpired');
    }, secondsUntilExpiration);

    if (this.expirationWarningTimeout) {
      clearTimeout(this.expirationWarningTimeout);
    }
    this.expirationWarningTimeout = setTimeout(() => {
      this.sessionWarning.emit({ expiration });
    }, secondsUntilWarning);
  }

  public clear(reason?: string, redirectTo?: string): void {
    this.setToken(undefined);
    this.setSession(undefined);
    this.groupService.clear(reason);
    this.loggedout.emit({ clear: !!reason, reason, redirectTo });
    this.fullStoryService.anonymize();
    FieldService.clearCache();
    this.accountService.clearAccountCache();
  }

  public async getSession(refresh?: boolean): Promise<Session> {
    if (this.sessionGuard.isStale() || refresh) {
      if (!this.sessionGuard.isInProgress()) {
        const sessionPromise = this.get<Session>('/session');
        this.sessionGuard.request = sessionPromise;
      }
      const session = await this.sessionGuard.request;
      this.setSession(session);
    }
    return this.sessionGuard.get();
  }

  public hasProjectAccess(session: Session, projectId: string) {
    return (
      session &&
      session.info2.permissions.filter(
        (perm) => perm.permission_type === 'EXTERNAL' && perm.external_id === projectId
      ).length > 0
    );
  }

  public canEditConfigurationRoute() {
    return !this.isReadOnly;
  }

  public canCloneConfiguration(session: Session, configId) {
    return (
      !this.isReadOnly &&
      (this.isAccountAdmin ||
        session.info2.permissions.some(
          (perm) => perm.config_id === configId && perm.permission_type === 'CONFIGURATION'
        ))
    );
  }

  public canEditConfiguration(session: Session, configId) {
    return (
      !this.isReadOnly &&
      (this.isAccountAdmin ||
        session.info2.permissions.some(
          (perm) => perm.config_id === configId && perm.permission_type === 'CONFIGURATION'
        ))
    );
  }

  public addLogoutInterceptor(interceptor: LogoutInterceptor) {
    this.logoutInterceptors.push(interceptor);
  }

  public removeLogoutInterceptor(interceptor: LogoutInterceptor) {
    this.logoutInterceptors = this.logoutInterceptors.filter((ic) => ic !== interceptor);
  }
}

@EnableClassTelemetry()
@Injectable()
export class AuthGuardService {
  constructor(
    protected router: Router,
    protected authService: AuthService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    if (!this.authService.isLoggedIn()) {
      // save where the user was trying to go to
      const appVolatile = (window as any).appVolatile;
      if (!appVolatile.redirectTo) {
        appVolatile.redirectTo = state.url;
      }

      // send them back to login
      this.router.navigate(['/'], {
        queryParams: {
          ...route.queryParams,
          groupId: route.paramMap.get('groupId'),
          dashboardAlias: route.paramMap.get('dashboardAlias'),
          aliasType: route.paramMap.has('dashboardAlias') ? 'dashboard' : null,
        },
      });
      return Promise.resolve(false);
    }

    return Promise.resolve(true);
  }
}

@EnableClassTelemetry()
@Injectable()
export class AdminAuthGuardService extends AuthGuardService {
  constructor(router: Router, authService: AuthService) {
    super(router, authService);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    if (super.canActivate(route, state)) {
      return this.authService.getSession().then(() => {
        const isAdmin = this.authService.isAccountAdmin;

        if (!isAdmin) {
          this.router.navigate(['/']);
        }

        return isAdmin;
      });
    } else {
      return Promise.resolve(false);
    }
  }
}

@EnableClassTelemetry()
@Injectable()
export class NotExecutiveGuardService {
  constructor(
    protected router: Router,
    protected authService: AuthService,
    protected groupService: GroupService
  ) {}

  canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Promise<boolean> {
    return this.authService.getSession().then(() => {
      const isExecutive = this.authService.isExecutive;

      if (isExecutive) {
        this.router.navigate([`groups/${this.groupService.getSelectedGroupId(true)}/projects`]);
      }

      return !isExecutive;
    });
  }
}

@EnableClassTelemetry()
@Injectable()
export class AccountFeatureGuardService {
  constructor(
    private accountService: SemantriaAccountService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Promise<boolean> {
    return this.accountService.sub.latest().then((v) => {
      const requiredFeature = route.data.feature as string;

      const hasFeature = v.features.includes(requiredFeature);
      if (!hasFeature) {
        this.router.navigate(['/']);
      }

      return hasFeature;
    });
  }
}
